import { ensureCentralPortalRuneStructure } from './PortalStructureMaintainer';
import { propagateInstabilityUpEngine, tickBlockUpdatesEngine, processUnloadedColumnEngine } from './FallingBlockEngine';
import { generateChunkData } from './TerrainGenerator';
import { CHUNK_SIZE, TileType, FallingBlock, HIEROGLYPH_COUNT, COMBO_MAX_LENGTH, NestOccupant, NestRespawn, RATS_NEST_TIMER, RodentType, UNDERGROUND_LEVEL, isApple, EXPLORATION_HOUSE_CENTER_X, SIGN_GLYPH_COUNT, EYE_SWITCH_DURATION_TICKS, SPAWN_CENTER_X, isExpandedKiliFruit } from './constants';

// ============================================================
// WORLD DATA STRUCTURE
// Shared between client and server
// ============================================================

/**
 * Calculate terrain surface Y at a given world X coordinate.
 * Uses 3 prime-frequencied sine waves for natural-looking terrain,
 * plus ocean depressions past the outermost dungeons.
 *
 * Ocean zones (aligned with cosmology.png):
 *   |x| < 1120:   Normal terrain (spawn area + all 6 dungeons)
 *   |x| >= 1120:   Ocean depression (~30 tiles deeper), 40-block smoothstep ramp-in.
 *                   No ramp-out — extends into ice wall (pixel 3/69, tile ±1320)
 *                   and ouroboros (pixel 2/70, tile ±1360) where cosmology zone
 *                   fill blocks create an abrupt transition.
 *
 * Derivation: cosmology ocean inner edge = pixel 64 (right) / pixel 8 (left).
 *   tile = (64 - CENTER_PX) * PIXELS_TO_TILES = (64 - 36) * 40 = 1120.
 *   Outermost dungeons at pixel 62/10 → tile ±1040, so oceans are past all dungeons.
 */
export function calculateSurfaceY(worldX: number, seed: number): number {
    const wave1 = Math.sin(worldX * 0.03 + seed * 0.001) * 4.5;
    const wave2 = Math.sin(worldX * 0.07 + seed * 0.002) * 3;
    const wave3 = Math.sin(worldX * 0.13 + seed * 0.003) * 1.5;
    let surface = wave1 + wave2 + wave3 + 4;

    // Ocean depression at |x| >= 1120 (cosmology ocean inner edge).
    // Extends well into ice wall and ouroboros cells with no ramp-out,
    // so the cosmology zone fill (KIWEN_LETE / KASI_ALE) overlaps abruptly.
    const absX = Math.abs(worldX);
    const OCEAN_START = 1120;  // inner edge of cosmology ocean zone
    const OCEAN_RAMP = 40;     // smoothstep transition width
    if (absX >= OCEAN_START) {
        const d = absX - OCEAN_START;
        let t: number;
        if (d < OCEAN_RAMP) {
            // Smoothstep ramp into ocean depression
            const r = d / OCEAN_RAMP;
            t = r * r * (3 - 2 * r);
        } else {
            // Full depression — no ramp out, continues into ice wall/ouroboros
            t = 1;
        }

        if (t > 0) {
            // Ocean floor curvature (~30 ± 18)
            const vwave1 = Math.sin(worldX * 0.02 + seed * 0.004) * 8;
            const vwave2 = Math.sin(worldX * 0.05 + seed * 0.005) * 6;
            const vwave3 = Math.sin(worldX * 0.11 + seed * 0.006) * 4;
            surface += (30 + vwave1 + vwave2 + vwave3) * t;
        }
    }

    return Math.floor(surface);
}

/**
 * Calculate the base terrain surface Y WITHOUT ocean depression.
 */
export function calculateBaseSurfaceY(worldX: number, seed: number): number {
    const wave1 = Math.sin(worldX * 0.03 + seed * 0.001) * 4.5;
    const wave2 = Math.sin(worldX * 0.07 + seed * 0.002) * 3;
    const wave3 = Math.sin(worldX * 0.13 + seed * 0.003) * 1.5;
    return Math.floor(wave1 + wave2 + wave3 + 4);
}

/**
 * Get the flat water level for the ocean at worldX.
 * Returns null if worldX is not inside an ocean zone.
 * Ocean starts at |x| = 1120, aligned with cosmology ocean inner edge.
 * Water level matches UNDERGROUND_LEVEL — the same Y where the sky turns black.
 */
export function getValleyWaterLevel(worldX: number): number | null {
    const absX = Math.abs(worldX);
    if (absX < 1120) return null; // no ocean before outermost dungeons
    return UNDERGROUND_LEVEL;
}

/**
 * Calculate the portal platform Y at SPAWN_CENTER_X by scanning columns ±5
 * and returning the minimum surfaceY (highest elevation), so the
 * platform sits at the highest natural terrain point in the range.
 */
export function calculatePortalSurfaceY(seed: number): number {
    let minY = calculateSurfaceY(SPAWN_CENTER_X, seed);
    for (let dx = -5; dx <= 5; dx++) {
        const y = calculateSurfaceY(SPAWN_CENTER_X + dx, seed);
        if (y < minY) minY = y;
    }
    return minY - 1;
}

/**
 * Calculate the exploration house platform Y by scanning columns around
 * EXPLORATION_HOUSE_CENTER_X and returning the minimum surfaceY (highest
 * elevation) minus 1, so the platform sits at the highest natural terrain point.
 */
export function calculateExplorationHouseSurfaceY(seed: number): number {
    const cx = EXPLORATION_HOUSE_CENTER_X;
    let minY = calculateSurfaceY(cx, seed);
    for (let dx = -5; dx <= 5; dx++) {
        const y = calculateSurfaceY(cx + dx, seed);
        if (y < minY) minY = y;
    }
    return minY - 1;
}

/** Returns the room boxes for spawn platform + exploration house. */
export function getRoomBoxes(seed: number): import('./constants').RoomBox[] {
    const p = calculatePortalSurfaceY(seed);
    const eh = calculateExplorationHouseSurfaceY(seed);
    const ehX1 = EXPLORATION_HOUSE_CENTER_X - 5;
    const ehX2 = EXPLORATION_HOUSE_CENTER_X + 5;
    const sc = SPAWN_CENTER_X;
    return [
        // Spawn house lower room: between x=±5 walls, roof down to platform floor
        { x1: sc - 5, y1: p - 7, x2: sc + 5, y2: p },
        // Spawn house upper room: between x=-1 and x=5 columns, lower roof to upper roof
        { x1: sc - 1, y1: p - 14, x2: sc + 5, y2: p - 7 },
        // Exploration house: single room, roof to platform
        { x1: ehX1, y1: eh - 7, x2: ehX2, y2: eh },
        // Ladder shaft: from platform floor down to underground level
        { x1: sc + 3, y1: p, x2: sc + 5, y2: UNDERGROUND_LEVEL },
    ];
}

/** Check if a position falls inside any of the spawn platform room boxes. */
export function isInSpawnProtection(x: number, y: number, roomBoxes: import('./constants').RoomBox[]): boolean {
    if (!roomBoxes || roomBoxes.length === 0) return false;
    return roomBoxes.some(box =>
        x >= box.x1 && x <= box.x2 &&
        y >= box.y1 && y <= box.y2
    );
}

// Helper to check if a tile type is affected by gravity
function isFallingType(tileType: TileType): boolean {
    return tileType === TileType.DIRT || tileType === TileType.GRASS || tileType === TileType.SKULL || tileType === TileType.STICK || tileType === TileType.LOG || tileType === TileType.FLINT || tileType === TileType.HEART_CONTAINER || tileType === TileType.KIWEN_ANPA || tileType === TileType.KIWEN_SOWELI || isExpandedKiliFruit(tileType);
}

export class WorldData {
    chunks: Map<string, number[][]>;
    blockUpdateLists: Map<string, FallingBlock[]>;
    runeData: Map<string, number>;  // "x,y" -> hieroglyphIndex
    nestData: Map<string, NestOccupant[]>;  // "x,y" -> occupants inside that nest block
    diceData: Map<string, number>;  // "x,y" -> dice face value (1-6)
    diceRollCount: number;           // incremental counter for dice rolls
    // Kiwen block metadata
    commandBlockData: Map<string, { words: number[]; voice: string }>;  // "x,y" -> stored speech
    commandBlockCooldown: Map<string, number>;  // "x,y" -> last replay tick
    signData: Map<string, number>;  // "x,y" -> glyph index (0-119)
    questionBlockInert: Set<string>;  // "x,y" -> used ? blocks
    eyeSwitchData: Map<string, number>;  // "x,y" -> activation tick
    pressurePlateActive: Set<string>;  // "x,y" -> currently active plates
    seed: number;

    constructor(seed: number = Date.now()) {
        this.chunks = new Map();
        this.blockUpdateLists = new Map();
        this.runeData = new Map();
        this.nestData = new Map();
        this.diceData = new Map();
        this.diceRollCount = 0;
        this.commandBlockData = new Map();
        this.commandBlockCooldown = new Map();
        this.signData = new Map();
        this.questionBlockInert = new Set();
        this.eyeSwitchData = new Map();
        this.pressurePlateActive = new Set();
        this.seed = seed;
    }

    getChunkKey(cx: number, cy: number): string {
        return `${cx},${cy}`;
    }

    parseChunkKey(key: string): { cx: number; cy: number } {
        const [cx, cy] = key.split(',').map(Number);
        return { cx, cy };
    }

    // Seeded random for consistent terrain generation
    seededRandom(x: number, y: number): number {
        const n = Math.sin(x * 12.9898 + y * 78.233 + this.seed) * 43758.5453;
        return n - Math.floor(n);
    }

    generateChunk(cx: number, cy: number): number[][] {
        return generateChunkData(this, cx, cy, calculateSurfaceY, calculatePortalSurfaceY, getValleyWaterLevel);
    }

    getChunk(cx: number, cy: number): number[][] {
        const key = this.getChunkKey(cx, cy);
        if (!this.chunks.has(key)) {
            this.chunks.set(key, this.generateChunk(cx, cy));
        }
        return this.chunks.get(key)!;
    }

    setChunk(cx: number, cy: number, tiles: number[][]): void {
        const key = this.getChunkKey(cx, cy);
        this.chunks.set(key, tiles);
    }

    hasChunk(cx: number, cy: number): boolean {
        return this.chunks.has(this.getChunkKey(cx, cy));
    }

    /** Iterate over all loaded chunks. Callback receives chunk coords and tile array. */
    forEachChunk(callback: (cx: number, cy: number, tiles: number[][]) => void): void {
        for (const [key, tiles] of this.chunks) {
            const [cxStr, cyStr] = key.split(',');
            callback(parseInt(cxStr, 10), parseInt(cyStr, 10), tiles);
        }
    }

    getBlockUpdateList(cx: number, cy: number): FallingBlock[] {
        const key = this.getChunkKey(cx, cy);
        if (!this.blockUpdateLists.has(key)) {
            this.blockUpdateLists.set(key, []);
        }
        return this.blockUpdateLists.get(key)!;
    }

    getTile(worldX: number, worldY: number): TileType {
        const cx = Math.floor(worldX / CHUNK_SIZE);
        const cy = Math.floor(worldY / CHUNK_SIZE);
        const localX = ((worldX % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
        const localY = ((worldY % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;

        const chunk = this.getChunk(cx, cy);
        return chunk[localY][localX];
    }

    setTile(worldX: number, worldY: number, tileType: TileType): void {
        const cx = Math.floor(worldX / CHUNK_SIZE);
        const cy = Math.floor(worldY / CHUNK_SIZE);
        const localX = ((worldX % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
        const localY = ((worldY % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;

        const chunk = this.getChunk(cx, cy);
        chunk[localY][localX] = tileType;
    }

    addFallingBlock(worldX: number, worldY: number, inertiaDir?: number, justAdded: boolean = false): void {
        const cx = Math.floor(worldX / CHUNK_SIZE);
        const cy = Math.floor(worldY / CHUNK_SIZE);
        const list = this.getBlockUpdateList(cx, cy);

        const exists = list.some(b => b.x === worldX && b.y === worldY);
        if (!exists) {
            list.push({ x: worldX, y: worldY, movedThisTick: false, inertiaDir, justAdded });
        }
    }

    removeFallingBlock(worldX: number, worldY: number): void {
        const cx = Math.floor(worldX / CHUNK_SIZE);
        const cy = Math.floor(worldY / CHUNK_SIZE);
        const list = this.getBlockUpdateList(cx, cy);

        const index = list.findIndex(b => b.x === worldX && b.y === worldY);
        if (index !== -1) {
            list.splice(index, 1);
        }
    }

    /** Check if a position is already tracked as a falling block. */
    hasFallingBlock(worldX: number, worldY: number): boolean {
        const cx = Math.floor(worldX / CHUNK_SIZE);
        const cy = Math.floor(worldY / CHUNK_SIZE);
        const list = this.getBlockUpdateList(cx, cy);
        return list.some(b => b.x === worldX && b.y === worldY);
    }

    /**
     * Check if connected leaves in one direction from centerX at rowY
     * have trunk support. Walk outward up to 3 blocks; at each leaf position,
     * scan downward through leaves looking for a TRUNK block.
     * Returns true if ANY downward scan finds a trunk (that side is supported).
     */
    hasTrunkSupportInDirection(centerX: number, rowY: number, dir: number): boolean {
        for (let dx = 1; dx <= 3; dx++) {
            const lx = centerX + dx * dir;
            if (this.getTile(lx, rowY) !== TileType.LEAVES) break;

            // Scan downward through leaves looking for TRUNK
            let checkY = rowY + 1;
            while (this.getTile(lx, checkY) === TileType.LEAVES) {
                checkY++;
            }
            if (this.getTile(lx, checkY) === TileType.TRUNK) {
                return true;
            }
        }
        return false;
    }

    /**
     * After a central leaf column evaporates to sticks, check connected branches.
     * For each evaporated row, check +/- 3 blocks for connected leaves.
     * Each direction is tested independently for trunk support via L-shaped search.
     * Unsupported branches are converted to falling sticks.
     */
    evaporateUnsupportedBranches(centerX: number, rowYs: number[], justAdded: boolean = false): void {
        // First pass: determine which positions need evaporation (before any changes)
        const toEvaporate: { x: number; y: number }[] = [];

        for (const rowY of rowYs) {
            // Check positive direction (+1 to +3)
            if (!this.hasTrunkSupportInDirection(centerX, rowY, 1)) {
                for (let dx = 1; dx <= 3; dx++) {
                    const lx = centerX + dx;
                    if (this.getTile(lx, rowY) !== TileType.LEAVES) break;
                    toEvaporate.push({ x: lx, y: rowY });
                }
            }
            // Check negative direction (-1 to -3)
            if (!this.hasTrunkSupportInDirection(centerX, rowY, -1)) {
                for (let dx = 1; dx <= 3; dx++) {
                    const lx = centerX - dx;
                    if (this.getTile(lx, rowY) !== TileType.LEAVES) break;
                    toEvaporate.push({ x: lx, y: rowY });
                }
            }
        }

        // Second pass: apply all evaporations
        for (const pos of toEvaporate) {
            const tileType = isApple(pos.x, pos.y) ? TileType.HEART_CONTAINER : TileType.STICK;
            this.setTile(pos.x, pos.y, tileType);
            this.addFallingBlock(pos.x, pos.y, undefined, justAdded);
        }
    }

    // When support is removed, propagate instability upward
    // Any dirt/grass blocks above that aren't already falling should start falling
    propagateInstabilityUp(worldX: number, worldY: number): void {
        propagateInstabilityUpEngine(this, worldX, worldY);
    }

    tickBlockUpdates(loadedChunks: { cx: number; cy: number }[]): { movedBlocks: FallingBlock[]; tileCorrections: { x: number; y: number; tileType: TileType }[] } {
        return tickBlockUpdatesEngine(this, loadedChunks);
    }

    // Process a single column in an unloaded chunk (bottom-up, one tile each)
    private processUnloadedColumn(
        x: number,
        cx: number,
        cy: number,
        loadedChunkKeys: Set<string>
    ): void {
        processUnloadedColumnEngine(this, x, cx, cy, loadedChunkKeys);
    }

    // Serialize chunk for network transfer
    serializeChunk(cx: number, cy: number): number[][] {
        return this.getChunk(cx, cy);
    }

    // Get chunks around a position
    getChunksAround(tileX: number, tileY: number, radius: number): { cx: number; cy: number }[] {
        const centerCx = Math.floor(tileX / CHUNK_SIZE);
        const centerCy = Math.floor(tileY / CHUNK_SIZE);
        const chunks: { cx: number; cy: number }[] = [];

        for (let cy = centerCy - radius; cy <= centerCy + radius; cy++) {
            for (let cx = centerCx - radius; cx <= centerCx + radius; cx++) {
                chunks.push({ cx, cy });
            }
        }

        return chunks;
    }

    // ============================================================
    // RUNE DATA METHODS
    // ============================================================

    private getRuneKey(x: number, y: number): string {
        return `${x},${y}`;
    }

    // Get hieroglyph index for a rune at position (returns 0 if not set)
    getRuneHieroglyph(x: number, y: number): number {
        const key = this.getRuneKey(x, y);
        return this.runeData.get(key) ?? 0;
    }

    // Set hieroglyph index for a rune at position
    setRuneHieroglyph(x: number, y: number, index: number): void {
        const key = this.getRuneKey(x, y);
        this.runeData.set(key, index % HIEROGLYPH_COUNT);
    }

    // Cycle hieroglyph to next value, returns new index
    cycleRuneHieroglyph(x: number, y: number): number {
        const current = this.getRuneHieroglyph(x, y);
        const next = (current + 1) % HIEROGLYPH_COUNT;
        this.setRuneHieroglyph(x, y, next);
        return next;
    }

    // ============================================================
    // DICE DATA
    // ============================================================

    /** Get dice face value at position (1-6, returns 1 if not set). */
    getDiceFace(x: number, y: number): number {
        const key = this.getRuneKey(x, y);
        return this.diceData.get(key) ?? 1;
    }

    /** Roll a dice at position using the world seed. Returns face value 1-6. */
    rollDiceAt(x: number, y: number): number {
        this.diceRollCount++;
        // Simple deterministic hash from seed + roll count
        let h = (this.seed ^ this.diceRollCount) >>> 0;
        h = Math.imul(h, 2654435761) >>> 0;
        h = (h ^ (h >>> 16)) >>> 0;
        const face = (h % 6) + 1;
        const key = this.getRuneKey(x, y);
        this.diceData.set(key, face);
        return face;
    }

    /** Remove dice data when tile is picked up. */
    removeDiceData(x: number, y: number): void {
        const key = this.getRuneKey(x, y);
        this.diceData.delete(key);
    }

    /** Set dice face value directly (used by persistence). */
    setDiceFace(x: number, y: number, face: number): void {
        const key = this.getRuneKey(x, y);
        this.diceData.set(key, face);
    }

    // ============================================================
    // COMMAND BLOCK DATA (KIWEN_LI)
    // ============================================================

    /** Store a speech command on a command block. */
    setCommandBlockSpeech(x: number, y: number, words: number[], voice: string): void {
        this.commandBlockData.set(`${x},${y}`, { words, voice });
    }

    /** Get stored speech for a command block. */
    getCommandBlockSpeech(x: number, y: number): { words: number[]; voice: string } | undefined {
        return this.commandBlockData.get(`${x},${y}`);
    }

    /** Remove command block data. */
    removeCommandBlockData(x: number, y: number): void {
        this.commandBlockData.delete(`${x},${y}`);
        this.commandBlockCooldown.delete(`${x},${y}`);
    }

    // ============================================================
    // SIGN DATA (KIWEN_NIMI)
    // ============================================================

    /** Get sign glyph index (0-119, returns 0 if not set). */
    getSignGlyph(x: number, y: number): number {
        return this.signData.get(`${x},${y}`) ?? 0;
    }

    /** Set sign glyph index. */
    setSignGlyph(x: number, y: number, index: number): void {
        this.signData.set(`${x},${y}`, ((index % SIGN_GLYPH_COUNT) + SIGN_GLYPH_COUNT) % SIGN_GLYPH_COUNT);
    }

    /** Cycle sign glyph to next value. Returns new index. */
    cycleSignGlyph(x: number, y: number): number {
        const current = this.getSignGlyph(x, y);
        const next = (current + 1) % SIGN_GLYPH_COUNT;
        this.setSignGlyph(x, y, next);
        return next;
    }

    /** Remove sign data. */
    removeSignData(x: number, y: number): void {
        this.signData.delete(`${x},${y}`);
    }

    // ============================================================
    // QUESTION BLOCK DATA (KIWEN_SEME)
    // ============================================================

    /** Check if a ? block has been used (inert). */
    isQuestionBlockInert(x: number, y: number): boolean {
        return this.questionBlockInert.has(`${x},${y}`);
    }

    /** Mark a ? block as used (inert). */
    markQuestionBlockInert(x: number, y: number): void {
        this.questionBlockInert.add(`${x},${y}`);
    }

    // ============================================================
    // EYE SWITCH DATA (KIWEN_LUKIN)
    // ============================================================

    /** Activate an eye switch (record activation tick). */
    activateEyeSwitch(x: number, y: number, tick: number): void {
        this.eyeSwitchData.set(`${x},${y}`, tick);
    }

    /** Check if an eye switch is currently active. */
    isEyeSwitchActive(x: number, y: number, currentTick: number): boolean {
        const activationTick = this.eyeSwitchData.get(`${x},${y}`);
        if (activationTick === undefined) return false;
        return (currentTick - activationTick) < EYE_SWITCH_DURATION_TICKS;
    }

    // ============================================================
    // RATS NEST DATA
    // ============================================================

    /** Get occupants inside a nest block at (x, y). */
    getNestOccupants(x: number, y: number): NestOccupant[] {
        return this.nestData.get(`${x},${y}`) ?? [];
    }

    /** Add a rodent occupant to a nest block. */
    addNestOccupant(x: number, y: number, occupant: NestOccupant): void {
        const key = `${x},${y}`;
        let occupants = this.nestData.get(key);
        if (!occupants) {
            occupants = [];
            this.nestData.set(key, occupants);
        }
        occupants.push(occupant);
    }

    /** Check if nestData has any entry (even empty) for this position.
     *  Used by terrain generator to avoid re-seeding a nest whose rat has left. */
    hasNestData(x: number, y: number): boolean {
        return this.nestData.has(`${x},${y}`);
    }

    /** Initialize an empty nest entry so the terrain generator won't re-seed it. */
    initNestEntry(x: number, y: number): void {
        const key = `${x},${y}`;
        if (!this.nestData.has(key)) {
            this.nestData.set(key, []);
        }
    }

    /**
     * Tick all nest timers. Decrements each occupant's timer by 1.
     * Returns respawn events for occupants whose timer reached 0 and removes them.
     * Only ticks nests within the given set of loaded chunk keys (if provided),
     * or all nests if no filter is given.
     */
    tickNests(loadedChunkKeys?: Set<string>): NestRespawn[] {
        const respawns: NestRespawn[] = [];
        for (const [key, occupants] of this.nestData) {
            // Optionally filter to loaded chunks
            if (loadedChunkKeys) {
                const [nx, ny] = key.split(',').map(Number);
                const cx = Math.floor(nx / CHUNK_SIZE);
                const cy = Math.floor(ny / CHUNK_SIZE);
                if (!loadedChunkKeys.has(`${cx},${cy}`)) continue;
            }

            // Check the nest block still exists
            const [nx, ny] = key.split(',').map(Number);
            if (this.getTile(nx, ny) !== TileType.RATS_NEST) {
                // Nest was destroyed — release all occupants immediately
                for (const occ of occupants) {
                    respawns.push({
                        nestX: nx, nestY: ny,
                        rodentType: occ.rodentType,
                        facing: occ.facing,
                        wallDir: occ.wallDir,
                        vertDir: occ.vertDir,
                    });
                }
                this.nestData.delete(key);
                continue;
            }

            const remaining: NestOccupant[] = [];
            for (const occ of occupants) {
                occ.timer--;
                if (occ.timer <= 0) {
                    respawns.push({
                        nestX: nx, nestY: ny,
                        rodentType: occ.rodentType,
                        facing: occ.facing,
                        wallDir: occ.wallDir,
                        vertDir: occ.vertDir,
                    });
                } else {
                    remaining.push(occ);
                }
            }

            // Keep the entry even when empty — an empty nestData entry marks
            // the nest as "already seeded", preventing re-seeding on chunk
            // regeneration. It also makes the chunk dirty for delta saves.
            this.nestData.set(key, remaining);
        }
        return respawns;
    }

    // Get portal combo code by reading runes from top to bottom
    // Returns array of hieroglyph indices, max COMBO_MAX_LENGTH (3)
    // Only includes runes that are part of a valid portal (topmost has SKY above)
    getPortalCode(x: number, y: number): number[] {
        const code: number[] = [];

        // First, find the topmost rune in the stack that has an open portal
        let topY = y;
        while (this.getTile(x, topY - 1) === TileType.RUNE) {
            topY--;
        }

        // Check if portal is open (2 tiles of SKY above topmost rune)
        if (!this.isPortalOpen(x, topY)) {
            return [];  // No valid portal
        }

        // Read runes from top to bottom, max COMBO_MAX_LENGTH
        let currentY = topY;
        while (code.length < COMBO_MAX_LENGTH && this.getTile(x, currentY) === TileType.RUNE) {
            code.push(this.getRuneHieroglyph(x, currentY));
            currentY++;
        }

        return code;
    }

    // Check if portal door can appear above a rune
    // Requires 2 tiles of SKY or PORTAL at y-1 and y-2
    isPortalOpen(x: number, y: number): boolean {
        const tile = this.getTile(x, y);
        if (tile !== TileType.RUNE) return false;

        const above1 = this.getTile(x, y - 1);
        const above2 = this.getTile(x, y - 2);

        // Portal is open if tiles above are passable (SKY or PORTAL frame)
        const isPassable = (t: TileType) => t === TileType.SKY || t === TileType.PORTAL;
        return isPassable(above1) && isPassable(above2);
    }

    // Find the topmost rune in a stack at position x, y
    // Returns y of topmost rune, or null if no rune at x,y
    findTopmostRune(x: number, y: number): number | null {
        if (this.getTile(x, y) !== TileType.RUNE) return null;

        let topY = y;
        while (this.getTile(x, topY - 1) === TileType.RUNE) {
            topY--;
        }
        return topY;
    }

    /**
     * Ensure the full spawn platform exists at world center.
     * Restores: 11-wide platform (x=-5..+5), rune at x=1 on roof level,
     * 2 portal frame tiles above the rune, trunk+opal pillars at x=±5,
     * and alternating roof connecting the pillar tops.
     * Called on player join to restore the portal even if destroyed.
     * @returns The position where rune was placed, or null if already correct
     */
    ensureCentralPortalRune(): { x: number; y: number } | null {
        return ensureCentralPortalRuneStructure(this, calculatePortalSurfaceY, calculateSurfaceY);
    }

}
